import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Map as MapIcon, RefreshCw } from 'lucide-react';
import { api } from '../../../services/api';
import { Tabs } from '../../../admin-ui';
import ServiceHeader from './ServiceHeader';
import ServicePlano from './ServicePlano';
import OrderStream from './OrderStream';
import MesaDrawer from './MesaDrawer';
import useServiceSocket from './useServiceSocket';
import { isLiveOrder, prepStationOf, urgencyTier } from './serviceColors';
import './service.css';

function collectOrders(tables) {
  return tables.flatMap((table) =>
    (table.currentSessionId?.orders || []).map((order) => ({
      ...order,
      tableId: order.tableId || { _id: table._id, number: table.number, label: table.label },
      table
    }))
  );
}

function orderMinutes(order) {
  const start = new Date(order.preparingAt || order.acceptedAt || order.createdAt || Date.now()).getTime();
  return Number.isFinite(start) ? Math.floor((Date.now() - start) / 60000) : 0;
}

function orderHasStation(order, station) {
  if (station === 'all') return true;
  return (order.items || []).some((item) => {
    const status = item.status || order.status;
    return prepStationOf(item) === station && !['served', 'paid', 'cancelled'].includes(status);
  });
}

export default function ServiceDashboard() {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTable, setSelectedTable] = useState(null);
  const [view, setView] = useState('orders');
  const [stationFilter, setStationFilter] = useState(() => localStorage.getItem('neon-service-station') || 'all');
  const [muted, setMuted] = useState(() => localStorage.getItem('neon-service-muted') === '1');
  const audioRef = useRef(null);

  const refresh = useCallback(() => {
    setError('');
    return api.get('/tables')
      .then((data) => {
        const next = data || [];
        setTables(next);
        setSelectedTable((prev) => prev ? next.find((table) => table._id === prev._id) || prev : prev);
      })
      .catch((err) => setError(err.message || 'No se pudo sincronizar servicio'))
      .finally(() => setLoading(false));
  }, []);

  const playDing = useCallback(() => {
    if (muted) return;
    try {
      if (!audioRef.current) audioRef.current = new Audio('/sounds/ding.mp3');
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } catch {
      // Browser audio policy can block notification sound.
    }
  }, [muted]);

  const playDoubleDing = useCallback(() => {
    if (muted) return;
    try {
      const first = new Audio('/sounds/ding.mp3');
      first.play().catch(() => {});
      setTimeout(() => {
        const second = new Audio('/sounds/ding.mp3');
        second.play().catch(() => {});
      }, 220);
    } catch {
      // Browser audio policy can block notification sound.
    }
  }, [muted]);

  const handleOpenRequested = useCallback((payload) => {
    playDoubleDing();
    // El plano y stream se actualizarán con el sync(); el toast/notif lo añadiremos cuando el componente Toast esté disponible globalmente
    if (payload?.tableId && payload?.table) {
      // No autoseleccionamos para no interrumpir, pero registramos en consola para debug
      console.info('[NEON] Mesa solicita apertura:', payload.table.number, payload.openRequest?.customerName);
    }
  }, [playDoubleDing]);

  const socketState = useServiceSocket({
    onSync: refresh,
    onOrderCreated: playDing,
    onOpenRequested: handleOpenRequested
  });

  useEffect(() => {
    refresh();
  }, [refresh]);

  const liveOrders = useMemo(
    () => collectOrders(tables)
      .filter(isLiveOrder)
      .sort((a, b) => {
        const urgency = orderMinutes(b) - orderMinutes(a);
        if (urgency !== 0) return urgency;
        return new Date(a.createdAt) - new Date(b.createdAt);
      })
      .slice(0, 50),
    [tables]
  );
  const stationCounts = useMemo(() => ({
    all: liveOrders.length,
    barra: liveOrders.filter((order) => orderHasStation(order, 'barra')).length,
    cocina: liveOrders.filter((order) => orderHasStation(order, 'cocina')).length
  }), [liveOrders]);
  const filteredOrders = useMemo(
    () => liveOrders.filter((order) => orderHasStation(order, stationFilter)),
    [liveOrders, stationFilter]
  );
  const deferredOrders = useDeferredValue(filteredOrders);

  const groups = useMemo(() => {
    const map = new Map();
    for (const order of deferredOrders) {
      const table = order.table;
      const key = table?._id || order.tableId?._id || order.tableId || 'unknown';
      if (!map.has(key)) map.set(key, { key, table, orders: [] });
      map.get(key).orders.push(order);
    }
    return [...map.values()];
  }, [deferredOrders]);

  const stats = useMemo(() => {
    const urgentOrders = liveOrders.filter((order) => urgencyTier(orderMinutes(order)) === 'critical').length;
    const openRequests = tables.filter((table) => table.status === 'open_requested').length;
    return {
      openTables: tables.filter((table) => table.active).length,
      liveOrders: liveOrders.length,
      urgentOrders,
      openRequests,
      totalOpen: tables.reduce((sum, table) => sum + Number(table.stats?.total || 0), 0)
    };
  }, [liveOrders, tables]);

  const changeStatus = async (order, status, scope = {}) => {
    setError('');
    try {
      await api.patch(`/orders/${order._id}/items/status`, {
        status,
        station: scope.station || stationFilter || 'all',
        itemIndexes: scope.itemIndexes
      });
      refresh();
    } catch (err) {
      setError(err.message || 'No se pudo cambiar el estado');
    }
  };

  const changeStationFilter = (next) => {
    setStationFilter(next);
    localStorage.setItem('neon-service-station', next);
  };

  const closeTable = async (table) => {
    await api.post(`/tables/${table._id}/close`);
  };

  const openTable = async (table) => {
    await api.post(`/tables/${table._id}/open`);
  };

  const toggleMuted = () => {
    setMuted((prev) => {
      const next = !prev;
      localStorage.setItem('neon-service-muted', next ? '1' : '0');
      return next;
    });
  };

  return (
    <div className="service-dashboard">
      <ServiceHeader
        stats={stats}
        muted={muted}
        onToggleMuted={toggleMuted}
        socketState={socketState}
        view={view}
        onViewChange={setView}
      />

      {error && <div className="service-error">{error}</div>}

      <div className="service-toolbar">
        <span>{loading ? 'Sincronizando servicio' : `${stats.totalOpen.toFixed(2)} € en mesas abiertas`}</span>
        <button type="button" onClick={refresh}>
          <RefreshCw size={16} />
          Sincronizar
        </button>
      </div>

      <div className="service-layout">
        <div className={view === 'plano' ? 'service-pane is-active' : 'service-pane service-pane-plano'}>
          <ServicePlano tables={tables} selectedTable={selectedTable} onSelectTable={setSelectedTable} />
        </div>
        <div className={view === 'orders' ? 'service-pane is-active' : 'service-pane service-pane-orders'}>
          <div className="service-station-tabs">
            <Tabs
              active={stationFilter}
              onChange={changeStationFilter}
              tabs={[
                { key: 'all', label: `Todo (${stationCounts.all})` },
                { key: 'barra', label: `Barra (${stationCounts.barra})` },
                { key: 'cocina', label: `Cocina (${stationCounts.cocina})` }
              ]}
            />
          </div>
          <OrderStream groups={groups} onStatusChange={changeStatus} stationFilter={stationFilter} />
        </div>
      </div>

      <button className="service-plano-fab" type="button" onClick={() => setView(view === 'orders' ? 'plano' : 'orders')}>
        <MapIcon size={18} />
        {view === 'orders' ? 'Plano' : 'Comandas'}
      </button>

      <MesaDrawer
        table={selectedTable}
        isOpen={Boolean(selectedTable)}
        onClose={() => setSelectedTable(null)}
        onOpenTable={openTable}
        onCloseTable={closeTable}
        onRefresh={refresh}
      />
    </div>
  );
}
